/**
 * The campaign form's values and rules, kept apart from React so they can be
 * tested on their own: blank and loaded values, validation, and the create /
 * edit request bodies. The schedule is typed in Eastern time and leaves here
 * as UTC instants.
 */
import {
  CAMPAIGN_PERCENT_MAX,
  CAMPAIGN_PERCENT_MIN,
  easternWallClockToInstant,
  instantToEasternWallClock,
  TicketSalesCampaignDiscountType,
  type CampaignDiscount,
  type CampaignSchedule,
  type TicketSalesCampaign,
  type TicketSalesCampaignCreateRequest,
  type TicketSalesCampaignPatchRequest,
} from '@bonsai/shared';

import { addDays, type DateKey } from '../../../shared/date-time-range/date-keys';
import type { DateTimePoint, DateTimeRange } from '../../../shared/date-time-range/date-time-range';
import {
  END_OF_DAY,
  START_OF_DAY,
  timeOfDayToClock,
} from '../../../shared/date-time-range/time-of-day';
import { discountValueToText, parseDiscountText } from '../../../utils/discount-amount-text';

export interface CampaignFormValues {
  name: string;
  description: string;
  discountType: TicketSalesCampaignDiscountType;
  /** As typed: whole percent, or dollars and cents for a flat amount. */
  amount: string;
  /** Eastern wall clock. */
  schedule: DateTimeRange;
  enabled: boolean;
}

export interface CampaignFormErrors {
  name?: string;
  description?: string;
  amount?: string;
  schedule?: string;
  /** A problem with the request as a whole rather than one field. */
  form?: string;
}

const NEW_CAMPAIGN_LENGTH_DAYS = 7;

/** Today's date on the Eastern calendar. */
export function easternToday(now: Date): DateKey {
  return instantToEasternWallClock(now).slice(0, 10);
}

/** A new campaign: starts at midnight tomorrow and runs to the end of the seventh day. */
export function blankCampaignForm(now: Date): CampaignFormValues {
  const today = easternToday(now);
  return {
    name: '',
    description: '',
    discountType: TicketSalesCampaignDiscountType.PERCENT,
    amount: '',
    schedule: {
      start: { date: addDays(today, 1), minutes: START_OF_DAY },
      end: { date: addDays(today, NEW_CAMPAIGN_LENGTH_DAYS), minutes: END_OF_DAY },
    },
    enabled: true,
  };
}

function instantToPoint(instant: string): DateTimePoint {
  const [date, clock] = instantToEasternWallClock(instant).split('T');
  const [hour, minute] = clock.split(':').map(Number);
  return { date, minutes: hour * 60 + minute };
}

export function pointToInstant(point: DateTimePoint): Date {
  return (
    easternWallClockToInstant(`${point.date}T${timeOfDayToClock(point.minutes)}`) ?? new Date(NaN)
  );
}

export function campaignToForm(campaign: TicketSalesCampaign): CampaignFormValues {
  return {
    name: campaign.name,
    description: campaign.description ?? '',
    discountType: campaign.discountType,
    amount: discountValueToText(campaign.discountType, campaign.discountValue),
    schedule: { start: instantToPoint(campaign.startsAt), end: instantToPoint(campaign.endsAt) },
    enabled: campaign.enabled,
  };
}

/** The stored discount value — whole percent, or cents — or null when the amount is not valid. */
export function discountValueOf(
  type: TicketSalesCampaignDiscountType,
  amount: string,
): number | null {
  const value = parseDiscountText(type, amount);
  if (value === null) return null;
  if (type === TicketSalesCampaignDiscountType.PERCENT) {
    return value >= CAMPAIGN_PERCENT_MIN && value <= CAMPAIGN_PERCENT_MAX ? value : null;
  }
  return value > 0 ? value : null;
}

/** The discount as entered, or null while the amount is not valid. */
export function formDiscount(values: CampaignFormValues): CampaignDiscount | null {
  const discountValue = discountValueOf(values.discountType, values.amount);
  return discountValue === null ? null : { discountType: values.discountType, discountValue };
}

export function isScheduleInOrder(values: CampaignFormValues): boolean {
  return (
    pointToInstant(values.schedule.end).getTime() > pointToInstant(values.schedule.start).getTime()
  );
}

/** The schedule as instants, or null while the end is not after the start. */
export function formSchedule(values: CampaignFormValues): CampaignSchedule | null {
  if (!isScheduleInOrder(values)) return null;
  return {
    startsAt: pointToInstant(values.schedule.start),
    endsAt: pointToInstant(values.schedule.end),
    enabled: values.enabled,
  };
}

export function validateCampaignForm(values: CampaignFormValues): CampaignFormErrors {
  const errors: CampaignFormErrors = {};
  if (!values.name.trim()) errors.name = 'Give the campaign a name.';
  if (!formDiscount(values)) {
    errors.amount =
      values.discountType === TicketSalesCampaignDiscountType.PERCENT
        ? `Enter a percentage from ${CAMPAIGN_PERCENT_MIN} to ${CAMPAIGN_PERCENT_MAX}.`
        : 'Enter an amount greater than $0.';
  }
  if (!isScheduleInOrder(values)) errors.schedule = 'The end has to be after the start.';
  return errors;
}

export const hasErrors = (errors: CampaignFormErrors) => Object.keys(errors).length > 0;

/** Keeps the banner text on one line, as the booking site shows it. */
export const singleLine = (text: string) => text.replace(/[\r\n]+/g, ' ');

interface NormalizedCampaign {
  name: string;
  description: string | null;
  discountType: TicketSalesCampaignDiscountType;
  discountValue: number | null;
  startsAt: string;
  endsAt: string;
  enabled: boolean;
}

function normalize(values: CampaignFormValues): NormalizedCampaign {
  const isoOf = (p: DateTimePoint) => {
    const instant = pointToInstant(p);
    return Number.isNaN(instant.getTime()) ? '' : instant.toISOString();
  };
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    discountType: values.discountType,
    discountValue: discountValueOf(values.discountType, values.amount),
    startsAt: isoOf(values.schedule.start),
    endsAt: isoOf(values.schedule.end),
    enabled: values.enabled,
  };
}

/** The create body. Call only once the form validates. */
export function toCreateRequest(values: CampaignFormValues): TicketSalesCampaignCreateRequest {
  const n = normalize(values);
  return { ...n, discountValue: n.discountValue ?? 0 };
}

/**
 * Only the fields that differ from `base`, or null when nothing does. A changed
 * type or amount sends both, since the number means something else under each
 * type.
 */
export function toPatchRequest(
  base: CampaignFormValues,
  values: CampaignFormValues,
): TicketSalesCampaignPatchRequest | null {
  const before = normalize(base);
  const after = normalize(values);
  const patch: TicketSalesCampaignPatchRequest = {};
  if (after.name !== before.name) patch.name = after.name;
  if (after.description !== before.description) patch.description = after.description;
  if (after.discountType !== before.discountType || after.discountValue !== before.discountValue) {
    patch.discountType = after.discountType;
    patch.discountValue = after.discountValue ?? 0;
  }
  if (after.startsAt !== before.startsAt) patch.startsAt = after.startsAt;
  if (after.endsAt !== before.endsAt) patch.endsAt = after.endsAt;
  if (after.enabled !== before.enabled) patch.enabled = after.enabled;
  return Object.keys(patch).length > 0 ? patch : null;
}

export const isCampaignFormDirty = (base: CampaignFormValues, values: CampaignFormValues) =>
  toPatchRequest(base, values) !== null;

const SERVER_FIELD_TO_FORM: Record<string, keyof CampaignFormErrors> = {
  name: 'name',
  description: 'description',
  discountType: 'amount',
  discountValue: 'amount',
  startsAt: 'schedule',
  endsAt: 'schedule',
};

/** The API's field errors, placed on the form field each one is about. */
export function serverErrorsToForm(fieldErrors: Record<string, string>): CampaignFormErrors {
  const errors: CampaignFormErrors = {};
  for (const [field, message] of Object.entries(fieldErrors)) {
    const key = SERVER_FIELD_TO_FORM[field] ?? 'form';
    errors[key] ??= message;
  }
  return errors;
}
